import axios from 'axios';
import api from '../utils/api';
import {getDriveApiToken} from '../lib/auth-token';

export type TransferStatus = 'pending' | 'uploading' | 'processing' | 'paused' | 'cancelling' | 'cancelled' | 'completed' | 'error';
export interface TransferProgress { status: TransferStatus; progress: number; speed: number; }
interface Input { batchId?: string; id: string; file: File; folderId?: string; path?: string; onProgress: (progress: TransferProgress) => void; }
interface Session { operationKey: string; uploadId?: string; storagePath?: string; checksum?: string; folderId?: string; }
interface Task { input: Input; controller: AbortController; paused: boolean; cancelled: boolean; session: Session;
  resolve: (value: any) => void; reject: (error: any) => void; key: string; }
const cancelled = () => Object.assign(new Error('Upload cancelled'), {code: 'ERR_CANCELED'});
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

class UploadTransport {
  private tasks = new Map<string, Task>();
  private taskKeys = new Set<string>();
  private queue: string[] = [];
  private active = new Set<string>();
  private directories = new Map<string, Promise<string>>();
  enqueue(input: Input): Promise<any> {
    if (this.tasks.has(input.id)) return Promise.reject(new Error('Upload task already exists'));
    // Sessions are namespaced to the signed-in identity, never persisted with credentials.
    const token = getDriveApiToken() || '';
    let identity = '';
    try { const claims = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); if (claims.tenantId && claims.id) identity = `${claims.tenantId}:${claims.id}`; } catch { /* auth API remains authoritative */ }
    if (!identity) return Promise.reject(new Error('Sign in again before uploading'));
    const key = `drive-transfer-v2:${identity}:${input.folderId || 'root'}:${input.path || input.file.name}:${input.file.size}:${input.file.lastModified}`;
    if (this.taskKeys.has(key)) return Promise.reject(new Error('This file is already queued for this destination'));
    let session: Session = {operationKey: crypto.randomUUID()};
    try { session = JSON.parse(localStorage.getItem(key) || 'null') || session; } catch { /* storage unavailable */ }
    return new Promise((resolve, reject) => {
      this.tasks.set(input.id, {input, session, resolve, reject, key, controller: new AbortController(), paused: false, cancelled: false});
      this.taskKeys.add(key);
      this.queue.push(input.id); this.pump();
    });
  }
  pause(id: string) { const task = this.tasks.get(id); if (!task) return; task.paused = true; task.controller.abort(); task.input.onProgress({status: 'paused', progress: 0, speed: 0}); }
  resume(id: string) { const task = this.tasks.get(id); if (!task || !task.paused) return; task.paused = false; if (!this.active.has(id)) {task.controller = new AbortController(); this.queue.push(id); this.pump();} }
  cancel(id: string) {
    const task = this.tasks.get(id); if (!task) return;
    task.cancelled = true; task.paused = false; task.controller.abort();
    if (!this.active.has(id)) void this.finishCancellation(task);
  }
  private save(task: Task) { try {localStorage.setItem(task.key, JSON.stringify(task.session));} catch { /* transfers still work without resume persistence */ } }
  private clear(task: Task) {try {localStorage.removeItem(task.key);} catch { /* noop */ }}
  private pump() {
    while (this.active.size < 3 && this.queue.length) {
      const id = this.queue.shift()!; const task = this.tasks.get(id);
      if (!task || task.cancelled || task.paused || this.active.has(id)) continue;
      this.active.add(id); void this.run(task).finally(() => {this.active.delete(id); this.pump();});
    }
    if (!this.tasks.size) this.directories.clear();
  }
  private async finishCancellation(task: Task) {
    task.input.onProgress({status: 'cancelling', progress: 0, speed: 0});
    try {
      if (!task.session.uploadId) {
        const result = (await api.post('/files/upload/operations/cancel', {operationKey: task.session.operationKey})).data;
        if (result.status === 'completed') {this.complete(task, {file: result.file}); return;}
        if (result.uploadId) {Object.assign(task.session, {uploadId: result.uploadId, storagePath: result.storagePath}); this.save(task);}
      }
      if (task.session.uploadId) {
        try { await api.post('/files/upload/multipart/abort', task.session); }
        catch (error: any) {
          if (error.response?.data?.code !== 'UPLOAD_ALREADY_COMPLETED') throw error;
          const status = await this.status(task);
          this.complete(task, {file: status.file}); return;
        }
        for (let count = 0; count < 60; count++) {
          const status = await this.status(task);
          if (status.status === 'cancelled' || status.status === 'failed') break;
          if (status.status === 'completed') {this.complete(task, {file: status.file}); return;}
          if (count === 59) throw new Error('Cancellation is still pending on the server. Retry to reconcile.');
          await wait(1000);
        }
      }
      this.clear(task); task.input.onProgress({status: 'cancelled', progress: 0, speed: 0}); task.reject(cancelled());
    } catch (error) { task.input.onProgress({status: 'error', progress: 0, speed: 0}); task.reject(error); }
    finally {this.tasks.delete(task.input.id); this.taskKeys.delete(task.key);}
  }
  private complete(task: Task, result: any) {
    if (!result.file?.id) throw new Error('Upload result is unavailable; refresh the destination to reconcile');
    this.clear(task); task.input.onProgress({status: 'completed', progress: 100, speed: 0});
    this.tasks.delete(task.input.id); this.taskKeys.delete(task.key); task.resolve({...result, parentFolderId: result.file?.parentId ?? task.session.folderId ?? null});
  }
  private async run(task: Task) {
    try {
      const signal = task.controller.signal;
      if (task.cancelled || task.paused) throw cancelled();
      task.input.onProgress({status: 'uploading', progress: 0, speed: 0});
      task.session.folderId = task.session.folderId || await this.destination(task, signal); this.save(task);
      if (signal.aborted) throw cancelled();
      const result = task.input.file.size >= 16 * 1024 * 1024 ? await this.multipart(task) : await this.ordinary(task);
      this.complete(task, result);
    } catch (error: any) {
      if (task.cancelled) { await this.finishCancellation(task); return; }
      if (task.paused || task.controller.signal.aborted) {
        if (!task.paused) {task.controller = new AbortController(); this.queue.push(task.input.id);}
        return;
      }
      if (['UPLOAD_CANCELLED', 'UPLOAD_RESULT_NO_LONGER_AVAILABLE'].includes(error.response?.data?.code)) this.clear(task);
      task.input.onProgress({status: 'error', progress: 0, speed: 0}); this.tasks.delete(task.input.id); this.taskKeys.delete(task.key); task.reject(error);
    }
  }
  private async destination(task: Task, signal: AbortSignal) {
    let parent = task.input.folderId || 'root';
    const segments = (task.input.path || task.input.file.webkitRelativePath || '').split('/').slice(0, -1);
    for (const name of segments) {
      if (signal.aborted) throw cancelled();
      if (!name || name === '.' || name === '..') throw new Error('Invalid upload folder path');
      const key = `${task.key.split(':').slice(0, 3).join(':')}:${task.input.batchId || task.input.id}:${parent}/${name}`;
      let pending = this.directories.get(key);
      if (!pending) {
        pending = api.post('/folders/create', {name, parentId: parent}, {timeout: 20000}).then(response => {
          if (!response.data.folder?.id) throw new Error('Folder creation failed'); return response.data.folder.id as string;
        }).catch(error => {this.directories.delete(key); throw error;});
        this.directories.set(key, pending);
      }
      parent = await pending;
    }
    return parent;
  }
  private async ordinary(task: Task) {
    const form = new FormData(); form.append('file', task.input.file); form.append('folderId', task.session.folderId || 'root');
    const started = Date.now();
    const response = await api.post('/files/upload', form, {signal: task.controller.signal, timeout: 10 * 60 * 1000,
      headers: {'Idempotency-Key': task.session.operationKey, 'Content-Type': undefined},
      onUploadProgress: event => {
        const fraction = event.total ? Math.min(1, event.loaded / event.total) : 0;
        task.input.onProgress({status: fraction === 1 ? 'processing' : 'uploading', progress: fraction * 100,
          speed: task.input.file.size * fraction / Math.max(0.1, (Date.now() - started) / 1000)});
      }});
    return response.data;
  }
  private checksum(task: Task): Promise<string> {
    return new Promise((resolve, reject) => {
      const worker = new Worker(new URL('../workers/uploadHash.worker.ts', import.meta.url), {type: 'module'});
      const signal = task.controller.signal;
      const cleanup = () => {worker.terminate(); signal.removeEventListener('abort', abort);};
      const abort = () => {cleanup(); reject(cancelled());};
      signal.addEventListener('abort', abort, {once: true});
      worker.onmessage = event => {cleanup(); event.data.error ? reject(new Error(event.data.error)) : resolve(event.data.checksum);};
      worker.onerror = () => {cleanup(); reject(new Error('Unable to checksum file'));};
      if (signal.aborted) abort(); else worker.postMessage(task.input.file);
    });
  }
  private async status(task: Task) {return (await api.get('/files/upload/multipart/status', {params: {uploadId: task.session.uploadId, storagePath: task.session.storagePath}})).data;}
  private async multipart(task: Task) {
    const signal = task.controller.signal, file = task.input.file;
    const checksum = await this.checksum(task);
    if (task.session.checksum && task.session.checksum !== checksum) throw new Error('Selected file differs from the saved upload');
    task.session.checksum = checksum;
    this.save(task);
    if (!task.session.uploadId) {
      const initiated = (await api.post('/files/upload/multipart/initiate', {fileName: file.name, fileSize: file.size,
        contentType: file.type || 'application/octet-stream', folderId: task.session.folderId, checksum: task.session.checksum},
        {signal, headers: {'Idempotency-Key': task.session.operationKey}})).data;
      if (initiated.status === 'completed') return {file: initiated.file};
      Object.assign(task.session, {uploadId: initiated.uploadId, storagePath: initiated.storagePath}); this.save(task);
    }
    let status = await this.status(task);
    if (status.status === 'completed') return {file: status.file};
    if (['cancelled', 'failed', 'cancel_requested'].includes(status.status)) {if (status.status !== 'cancel_requested') this.clear(task); throw new Error(status.error || `Upload is ${status.status}`);}
    if (status.status === 'uploading') {
      const size = status.chunkSize || 8 * 1024 * 1024;
      const count = Math.ceil(file.size / size);
      const parts = new Map<number, string>((status.parts || []).map((part: any) => [part.PartNumber, part.ETag]));
      const loaded = new Map<number, number>();
      const started = Date.now(); let transferred = 0, next = 1;
      const publish = () => {
        const acknowledged = [...parts.keys()].reduce((sum, part) => sum + Math.min(size, file.size - (part - 1) * size), 0);
        const inFlight = [...loaded.values()].reduce((sum, value) => sum + value, 0);
        task.input.onProgress({status: 'uploading', progress: Math.min(100, (acknowledged + inFlight) / file.size * 100),
          speed: (transferred + inFlight) / Math.max(0.1, (Date.now() - started) / 1000)});
      };
      const send = async () => {
        while (next <= count) {
          const part = next++; if (parts.has(part)) continue;
          if (signal.aborted) throw cancelled();
          const body = file.slice((part - 1) * size, part * size);
          const url = (await api.post('/files/upload/multipart/presigned-url', {...task.session, partNumber: part}, {signal})).data.presignedUrl;
          // Raw axios: never send Drive authorization headers to object storage.
          const response = await axios.put(url, body, {signal, timeout: 10 * 60 * 1000, headers: {'Content-Type': file.type || 'application/octet-stream'},
            onUploadProgress: event => {loaded.set(part, Math.min(body.size, event.loaded)); publish();}});
          const etag = response.headers.etag; if (!etag) throw new Error('Object storage did not expose ETag');
          loaded.delete(part); parts.set(part, etag); transferred += body.size; publish();
        }
      };
      const transfers = await Promise.allSettled([send(), send()]);
      const failed = transfers.find(result => result.status === 'rejected') as PromiseRejectedResult | undefined;
      if (failed) throw failed.reason;
      status = (await api.post('/files/upload/multipart/complete?async=true', {...task.session,
        parts: [...parts].sort((a, b) => a[0] - b[0]).map(([PartNumber, ETag]) => ({PartNumber, ETag}))}, {signal})).data;
    }
    const deadline = Date.now() + 30 * 60 * 1000;
    while (status.status !== 'completed') {
      if (Date.now() > deadline) throw new Error('Upload verification is still pending. Retry to reconcile.');
      if (signal.aborted) throw cancelled();
      if (['failed', 'cancelled', 'cancel_requested'].includes(status.status)) {if (status.status !== 'cancel_requested') this.clear(task); throw new Error(status.error || `Upload is ${status.status}`);}
      task.input.onProgress({status: 'processing', progress: 100, speed: 0});
      await wait(1000); status = await this.status(task);
    }
    return {file: status.file};
  }
}
export default new UploadTransport();
